"use client"
import React, { useEffect, useRef } from 'react'

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    r: number
}

/**
 * 粒子网络背景：漂浮的节点 + 邻近节点连线，鼠标靠近时连线增强。
 * 纯 canvas 实现，无依赖，自动适配容器尺寸与高清屏。
 */
export default function ParticleField({
    className = '',
    density = 9000,      // 每多少平方像素一个粒子
    linkDist = 130,      // 连线距离阈值
    color = '129, 140, 248', // indigo-400
}: {
    className?: string
    density?: number
    linkDist?: number
    color?: string
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const mouseRef = useRef({ x: -9999, y: -9999 })

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        let particles: Particle[] = []
        let raf = 0
        let w = 0
        let h = 0
        const dpr = Math.min(window.devicePixelRatio || 1, 2)

        const resize = () => {
            const rect = canvas.parentElement!.getBoundingClientRect()
            w = rect.width
            h = rect.height
            canvas.width = w * dpr
            canvas.height = h * dpr
            canvas.style.width = `${w}px`
            canvas.style.height = `${h}px`
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

            const count = Math.min(Math.floor((w * h) / density), 120)
            particles = Array.from({ length: count }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.35,
                vy: (Math.random() - 0.5) * 0.35,
                r: Math.random() * 1.6 + 0.8,
            }))
        }

        const onMove = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect()
            mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
        }
        const onLeave = () => {
            mouseRef.current = { x: -9999, y: -9999 }
        }

        const tick = () => {
            ctx.clearRect(0, 0, w, h)
            const m = mouseRef.current

            for (const p of particles) {
                p.x += p.vx
                p.y += p.vy
                if (p.x < 0 || p.x > w) p.vx *= -1
                if (p.y < 0 || p.y > h) p.vy *= -1

                ctx.beginPath()
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
                ctx.fillStyle = `rgba(${color}, 0.55)`
                ctx.fill()
            }

            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const a = particles[i]
                    const b = particles[j]
                    const dx = a.x - b.x
                    const dy = a.y - b.y
                    const dist = Math.hypot(dx, dy)
                    if (dist < linkDist) {
                        // 鼠标靠近时连线变亮
                        const midX = (a.x + b.x) / 2
                        const midY = (a.y + b.y) / 2
                        const mDist = Math.hypot(midX - m.x, midY - m.y)
                        const boost = Math.max(0, 1 - mDist / 180)
                        const alpha = (1 - dist / linkDist) * (0.16 + boost * 0.4)

                        ctx.beginPath()
                        ctx.moveTo(a.x, a.y)
                        ctx.lineTo(b.x, b.y)
                        ctx.strokeStyle = `rgba(${color}, ${alpha})`
                        ctx.lineWidth = 1
                        ctx.stroke()
                    }
                }
            }

            raf = requestAnimationFrame(tick)
        }

        resize()
        tick()
        window.addEventListener('resize', resize)
        canvas.parentElement?.addEventListener('mousemove', onMove)
        canvas.parentElement?.addEventListener('mouseleave', onLeave)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('resize', resize)
            canvas.parentElement?.removeEventListener('mousemove', onMove)
            canvas.parentElement?.removeEventListener('mouseleave', onLeave)
        }
    }, [density, linkDist, color])

    return <canvas ref={canvasRef} className={`pointer-events-none absolute inset-0 ${className}`} />
}
